"use client";

import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { BsLinkedin } from "react-icons/bs";
import {
  FaFacebook,
  FaShareAlt,
  FaWhatsapp,
  FaTelegram,
  FaTimes,
  FaLink,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

export interface InfoCardProps {
  slug: string;
  infoType: string;
  title: string;
  description: string;
  imageUrl?: string;
}

export const InfoCard = ({
  slug,
  infoType,
  title,
  description,
  imageUrl,
}: InfoCardProps) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  // Clean HTML tags from description
  const cleanDescription = description
    .replace(/<[^>]*>/g, "")
    .substring(0, 150)
    .concat("...");

  const shareUrl =
    typeof window !== "undefined"
      ? `https://www.laplagemetaverse.org/info-center/${slug}`
      : "";
  const shareText = `Check out: ${title}`;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        shareMenuRef.current &&
        !shareMenuRef.current.contains(event.target as Node)
      ) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showShareMenu]);

  const handleShare = (platform: string) => {
    let url = "";

    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText
        )}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl
        )}`;
        break;
      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl
        )}`;
        break;
      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + shareUrl
        )}`;
        break;
      case "telegram":
        url = `https://t.me/share/url?url=${encodeURIComponent(
          shareUrl
        )}&text=${encodeURIComponent(shareText)}`;
        break;
    }

    if (url) {
      window.open(url, "_blank", "width=600,height=400");
      setShowShareMenu(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setShowShareMenu(false);
      }, 1500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <div className="overflow-hidden flex flex-col h-full border border-gray-200 rounded-lg hover:shadow-lg transition-shadow duration-300">
      {imageUrl && (
        <div className="w-full h-48 overflow-hidden bg-gray-100">
          <img
            src={imageUrl || "/placeholder.svg"}
            alt={title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs font-medium text-blue-600 uppercase ">
            {infoType}
          </p>
          <button
            onClick={() => setShowShareMenu(!showShareMenu)}
            className="text-primary rounded-md transition-colors duration-200 font-medium"
            title="Share this info"
          >
            <FaShareAlt />
          </button>
        </div>
        <h3 className="text-base mb-2 line-clamp-1">{title}...</h3>
        <p className="text-sm text-gray-600 mb-4 flex-1 line-clamp-3">
          {cleanDescription}
        </p>
        <div className="flex gap-2">
          <Link
            href={`/info-center/${slug}`}
            className="flex-1 w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center gap-2 font-medium"
          >
            {/* <button className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center gap-2 font-medium"> */}
            Read More
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            {/* </button> */}
          </Link>
          <div className="relative" ref={shareMenuRef}>
            {showShareMenu && (
              <div className="absolute right-0 bottom-full mb-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-700">
                    Share
                  </span>
                  <button
                    onClick={() => setShowShareMenu(false)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
                <button
                  onClick={handleCopyLink}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm transition-colors"
                >
                  <FaLink />
                  {copySuccess ? "Link Copied!" : "Copy Link"}
                </button>

                <button
                  onClick={() => handleShare("facebook")}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm transition-colors border-t border-gray-200"
                >
                  <FaFacebook />
                  Facebook
                </button>

                <button
                  onClick={() => handleShare("whatsapp")}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm transition-colors border-t border-gray-200"
                >
                  <FaWhatsapp />
                  WhatsApp
                </button>
                <button
                  onClick={() => handleShare("telegram")}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm transition-colors border-t border-gray-200"
                >
                  <FaTelegram />
                  Telegram
                </button>
                <button
                  onClick={() => handleShare("linkedin")}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm transition-colors border-t border-gray-200"
                >
                  <BsLinkedin />
                  LinkedIn
                </button>
                <button
                  onClick={() => handleShare("twitter")}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 text-sm transition-colors border-t border-gray-200"
                >
                  <FaXTwitter /> X (Twitter)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
